import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import { AuthRedirectService } from 'src/app/shared';
import { logValidationErrors } from 'src/app/terminals/shared/helpers';
import { PasswordValidationStatusResponse } from 'src/app/utils/cargo-driver-check-in-service/services/auth.service';
import { AuthService, CustomValidators } from '../../utils';
import { User } from '../../utils/cargo-driver-check-in-model/user';
import { UserProfileSignUpForm } from '../sign-up/utils/types/user-types';
import { FirebaseConfig } from './../../../environments/environment';
import { formErrors } from './form-errors';

@Component({
    selector: 'app-trucker-enrollment',
    templateUrl: './trucker-enrollment.component.html',
    styleUrls: ['./trucker-enrollment.component.scss'],
    standalone: false
})
export class TruckerEnrollmentComponent implements OnInit, OnDestroy {

    public truckerEnrollmentForm: FormGroup<UserProfileSignUpForm>;
    public sending: boolean = false;
    public alertMessage: string = '';
    private timerMessageError: number;
    public formErrors: any = formErrors;
    private unsubscribe$: Subject<void> = new Subject<void>();
    public passwordValidationResponse: PasswordValidationStatusResponse;

    constructor(
        private http: HttpClient,
        private route: ActivatedRoute,
        private authService: AuthService,
        private authRedirectService: AuthRedirectService
    ) { }

    ngOnInit(): void {
        this.createForm();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    createForm(): void {
        this.truckerEnrollmentForm = new FormGroup<UserProfileSignUpForm>({
            usr_password: new FormControl<string>('', { validators: Validators.required }),
            usr_password_confirm: new FormControl<string>('', { validators: Validators.required })
        }, {
            validators: <ValidatorFn>CustomValidators.match('usr_password', 'usr_password_confirm')
        }
        );
    }

    registerContact(event, form): void {
        this.passwordValidationResponse = null;
        event.preventDefault();

        if (form.invalid) {
            logValidationErrors(this.getControls());
            this.sending = false;
            return;
        }
        this.sending = true;

        this.authService.validatePassword(this.truckerEnrollmentForm.value.usr_password).pipe(
            switchMap(() => this.route.paramMap.pipe(
                take(1),
                switchMap((params) => {
                    const password = this.truckerEnrollmentForm.value.usr_password;
                    const body = {
                        password,
                        temp_trucker_contact_key: params.get('user_key')
                    };
                    const options = {
                        headers: new HttpHeaders({
                            'Content-Type': 'application/json'
                        })
                    };
                    const url = `${FirebaseConfig.functions}` + '/checkin/register-contact';

                    return this.http.post<any>(url, body, options)
                        .pipe(
                            switchMap(({ response }) => this.login(response.usr_email, password))
                        );
                })
            ))
        )
            .subscribe({
                next: () => {
                    this.sending = false;
                    this.redirectLogin();
                },
                error: (error) => {
                    this.sending = false;
                    if (!error.issues) {
                        const message = error && error.customMessage || 'Something went wrong!';
                        this.showAlertMessage(message);
                    }
                    this.passwordValidationResponse = error;

                }
            });
    }

    login(email, password): Observable<User> {
        return this.authService.login(email, password)
            .pipe(
                catchError(() => {
                    return throwError(() => ({
                        reason: 'login',
                        customMessage: 'Something went wrong when loggging in.'
                    }));
                })
            );
    }

    redirectLogin(): void {
        this.hideAlertMessage();
        this.authRedirectService.redirect();
    }

    hideAlertMessage(): void {
        window.clearTimeout(this.timerMessageError);
        this.alertMessage = '';
    }

    showAlertMessage(message: string): void {
        window.clearTimeout(this.timerMessageError);
        this.alertMessage = message;

        this.timerMessageError = window.setTimeout(() => {
            this.alertMessage = '';
        }, 5000);
    }

    getControls(): { [key: string]: AbstractControl; } {
        const controls: { [key: string]: AbstractControl; } = {};
        Object.keys(this.truckerEnrollmentForm.controls).forEach(key => {
            controls[key] = this.truckerEnrollmentForm.get(key);
        });
        return controls;
    }

}
